import React, { useState, useRef } from 'react';
import { Upload, X, Image, FileText, Video, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FileUpload = ({ 
  value, 
  onChange, 
  accept = "image/*,video/*,.pdf",
  placeholder = "Arrastra un archivo o haz clic para seleccionar",
  className = "",
  previewClassName = "h-32",
  showPreview = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máximo 100MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // The URL already includes /api prefix from the backend
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}${response.data.url}`;
      
      onChange(fullUrl);
      toast.success('Archivo subido correctamente');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  const getFileType = (url) => {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (lower.match(/\.(jpg|jpeg|png|gif|webp)$/)) return 'image';
    if (lower.match(/\.(mp4|webm|mov)$/)) return 'video';
    if (lower.match(/\.pdf$/)) return 'pdf';
    // Check by path
    if (lower.includes('/images/')) return 'image';
    if (lower.includes('/videos/')) return 'video';
    if (lower.includes('/documents/')) return 'pdf';
    return 'image'; // default
  };

  const fileType = getFileType(value);

  return (
    <div className={className}>
      {/* Preview */}
      {value && showPreview && (
        <div className={`relative mb-2 rounded-lg overflow-hidden bg-gray-100 ${previewClassName}`}>
          {fileType === 'image' && (
            <img 
              src={value} 
              alt="Preview" 
              className="w-full h-full object-cover"
            />
          )}
          {fileType === 'video' && (
            <video 
              src={value} 
              className="w-full h-full object-cover"
              controls
            />
          )}
          {fileType === 'pdf' && (
            <div className="w-full h-full flex items-center justify-center bg-red-50">
              <FileText className="w-12 h-12 text-red-500" />
            </div>
          )}
          
          {/* Remove button */}
          <button
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${dragActive ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-gray-200 hover:border-gray-300'}
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--accent))]" />
            <span className="text-sm text-gray-500">Subiendo...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <div className="flex items-center gap-2 text-gray-400">
              <Image className="w-5 h-5" />
              <Video className="w-5 h-5" />
              <FileText className="w-5 h-5" />
            </div>
            <span className="text-sm text-gray-500">{placeholder}</span>
            <span className="text-xs text-gray-400">
              {value ? 'Cambiar archivo' : 'Imágenes, videos o PDF (máx. 100MB)'}
            </span>
          </div>
        )}
      </div>

      {/* URL input as alternative */}
      {!uploading && (
        <div className="mt-2">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="O pega una URL directamente..."
            className="w-full text-xs p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
          />
        </div>
      )}
    </div>
  );
};

export default FileUpload;
